// mission-initializer-request.ts
//
// Tách THUẦN từ mission-initializer.ts (phase 10, lô A): bước 1 gốc — đọc +
// validate request file theo InitRequestSchema, rồi derive operation/mission id
// và các đường dẫn control-root/workspace. `initMission` gọi 1 hàm này thay cho
// khối đọc file + parse + throw trực tiếp.

import { join } from "node:path";
import { readText } from "../filesystem-helpers.js";
import { InitValidationError } from "./mission-initializer-errors.js";
import { deriveMissionId, deriveOperationId } from "./mission-initializer-ids.js";
import { controlRootPath, missionHomePath } from "./mission-resolver.js";
import { type InitRequest, InitRequestSchema } from "./schemas/init-schema.js";

export interface ResolvedInitRequest {
  request: InitRequest;
  operationId: string;
  missionId: string;
  missionHome: string;
  controlRoot: string;
  missionWorkspace: string;
  compatPath: string;
}

/**
 * 1. Validate request. .strict() already rejects extra fields (incl. a
 * self-declared "approval" field the model might try to inject) — test 7.
 *
 * Idempotency key (documented judgment call in evidence): InitRequestSchema
 * has NO explicit operation_id field. This module derives one deterministically
 * as sha256 of the canonical (sorted-key) JSON of the VALIDATED request —
 * same request content (incl. same blueprint hash/repo/base) => same
 * operation id => a killed-and-rerun init recognizes the mission it already
 * started (by checking whether `<mission_home>` for that operation id already
 * exists) instead of creating a second worktree/mission (tests 10/15).
 */
export async function resolveInitRequest(
  requestFilePath: string,
  workspaceRoot: string,
  now: () => Date,
): Promise<ResolvedInitRequest> {
  const rawText = await readText(requestFilePath);
  const rawJson = JSON.parse(rawText);
  const parsed = InitRequestSchema.safeParse(rawJson);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new InitValidationError(
      `Request file "${requestFilePath}" không hợp lệ theo init.schema: ${
        issue ? `${issue.path.join(".")}: ${issue.message}` : parsed.error.message
      }`,
    );
  }
  const request = parsed.data;
  const operationId = deriveOperationId(request);
  const missionId = deriveMissionId(operationId, now);
  const missionHome = missionHomePath(workspaceRoot, missionId);
  const controlRoot = controlRootPath(workspaceRoot, missionId);
  const missionWorkspace = join(missionHome, "workspace");
  const compatPath = join(controlRoot, "compatibility.json");
  return {
    request,
    operationId,
    missionId,
    missionHome,
    controlRoot,
    missionWorkspace,
    compatPath,
  };
}
